export function query (selector, parent = document) {
  return parent.querySelector(selector);
}

export function queryAll (selector, parent = document) {
  return Array.from(parent.querySelectorAll(selector));
}

// todo: check that padding and border are included, and add them if not, otherwise <body> needs to be styled in a very specific way:
//  margin:0; padding:0; and box-sizing:border-box; MUST be applied to everything
export function bounds (elem) {
  const rect = {};

  try {
    const clientRect = elem.getBoundingClientRect();
    rect.top = clientRect.top;
    rect.right = clientRect.right;
    rect.bottom = clientRect.bottom;
    rect.left = clientRect.left;
    rect.width = clientRect.right - clientRect.left;
    rect.height = clientRect.bottom - clientRect.top;
  } catch (e) {}

  return rect;
}

export function contains (a, b) {
  if (document.documentElement.contains) {
    return a !== b && (a.contains ? a.contains(b) : true);
  }

  if (document.documentElement.compareDocumentPosition) {
    return !!(a.compareDocumentPosition(b) & 16);
  }

  return false;
}

export function before (elem, beforeElem) {
  if (document.documentElement.insertAdjacentElement) {
    elem.insertAdjacentElement('beforebegin', beforeElem);
  } else {
    elem.parentNode.insertBefore(beforeElem, elem);
  }
}

export function after (elem, afterElem) {
  if (document.documentElement.insertAdjacentElement) {
    elem.insertAdjacentElement('afterend', afterElem);
  } else {
    elem.parentNode.insertBefore(afterElem, elem.nextSibling);
  }
}

export function delegate (selector, callback) {
  return function (event) {
    const closest = event.target.closest(selector);
    if (closest) {
      event.delegateTarget = closest;
      callback.call(this, event);
    }
  };
}

export function toggleHidden (elem, hidden = null) {
  if (elem) {
    enableAttribute(elem, 'hidden', hidden);
  }
}

export function toggleClass (elem, className, enabled = null) {
  if (elem) {
    elem.classList.toggle(className, enabled === null ? elem.classList.has(className) : enabled);
  }
}

export function enableAttribute (elem, attr, enabled = null) {
  if (elem) {
    const wasEnabled = elem.hasAttribute(attr);

    enabled = enabled === null ? wasEnabled : enabled;

    if (wasEnabled === enabled) {
      return false;
    }

    if (enabled) {
      elem.setAttribute(attr, true);
    } else {
      elem.removeAttribute(attr);
    }

    return wasEnabled !== enabled;
  }

  return false;
}

/**
 * Scroll element in to view
 * @param {Element} elem The element to scroll in to view
 */
export function scrollIntoView (elem) {
  if (typeof elem.scrollIntoViewIfNeeded !== 'undefined') {
    elem.scrollIntoViewIfNeeded();
  } else if (typeof elem.scrollIntoView !== 'undefined') {
    elem.scrollIntoView({
      behaviour: 'auto',
      block: 'nearest'
    });
  } else {
    // fallback, look for all scrollable parents, scroll each so that the element is visible
  }
}

const parser = new DOMParser();
export function createFromHtml (html, mime = 'text/html') {
  return parser.parseFromString(html, mime);
}

export async function waitUntil (elem, event) {
  return new Promise(function (resolve) {
    elem.addEventListener(event, resolve, { once: true });
  });
}

// a global ResizeObserver instance which triggers a custom "resize" event on observed nodes
// the bounds from the observer entries don't appear to match what we get from getBoundingClientRect
// so we pay a penalty here for calling getBoundingClientRect on each observed entry, but the hope that
// with only performing this on resize we should be paying this cost less than if we had to
// perform the call whenever we needed it
export const resizeObserver = new ResizeObserver(entries => {
  for (const entry of entries) {
    const resizeEvent = new CustomEvent('resize', {
      detail: {
        contentRect: entry.contentRect
      }
    });

    entry.target.dispatchEvent(resizeEvent);

    if (!resizeEvent.defaultPrevented && typeof entry.target.onresize === 'function') {
      entry.target.onresize(resizeEvent);
    }
  }
});

// @todo read https://github.com/ftlabs/fastclick/blob/main/lib/fastclick.js and implement changes where applicable

export class TouchTap {
  constructor (options = {}) {
    this._options = Object.assign({}, TouchTap.defaultOptions, options);
    this._element = null;
    this._trackingTap = false;
    this._targetElement = null;
    this._touchStartTime = 0;

    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchClick = this.onTouchClick.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchCancel = this.onTouchCancel.bind(this);
  }

  attach (element) {
    if (element._touchTap) {
      throw new Error('TouchTap already attached to this element');
    }

    this._element = element;
    this._element._touchTap = this;

    this._element.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this._element.addEventListener('click', this.onTouchClick);
    this._element.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this._element.addEventListener('touchend', this.onTouchEnd);
    this._element.addEventListener('touchcancel', this.onTouchCancel);
  }

  detach () {
    this._element.removeEventListener('touchstart', this.onTouchStart, { passive: true });
    this._element.removeEventListener('click', this.onTouchClick);
    this._element.removeEventListener('touchmove', this.onTouchMove, { passive: true });
    this._element.removeEventListener('touchend', this.onTouchEnd);
    this._element.removeEventListener('touchcancel', this.onTouchCancel);

    this._element._touchTap = null;
    this._element = null;
  }

  reset () {
    this._trackingTap = false;
    this._targetElement = null;
    this._touchStartTime = 0;
  }

  onTouchStart (event) {
    this._trackingTap = true;
    this._targetElement = event.target;
    this._touchStartTime = Date.now();
  }

  onTouchEnd (event) {
    if (!this._trackingTap) return true;

    const touchDuration = Date.now() - this._touchStartTime;

    if (touchDuration > this._options.threshold) {
      this.reset();
      return true;
    }

    const touchTapEvent = new TouchEvent('touchtap', {
      // bubbles may have perf implications since it needs to bubble the event up the tree
      // however without it we'd need to bind this util to every node we want to enable the tap event on
      bubbles: true,
      cancelable: true,
      touches: event.changedTouches,
      targetTouches: event.targetTouches,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey
    });

    this._targetElement.dispatchEvent(touchTapEvent);

    this.reset();

    // prevent the default action of the touchend if the touchtap event is prevented
    // this will also prevent the click event from firing
    if (touchTapEvent.defaultPrevented) {
      event.preventDefault();
    }
  }

  onTouchMove (event) {
    this.reset();
  }

  onTouchCancel (event) {
    this.reset();
  }

  onTouchClick (event) {
    this.reset();
  }

  static attach (element, options) {
    const touchTap = new TouchTap(options);
    touchTap.attach(element);
    return touchTap;
  }

  static detach (element) {
    if (element._touchTap) element._touchTap.detach();
  }
}

TouchTap.defaultOptions = {
  threshold: 150
};
